import React, { useEffect, useState } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  Button,
  useDisclosure,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Spinner,
  IconButton,
  VStack,
  Grid,
  GridItem,
  FormControl,
  FormLabel,
  HStack,
  Tooltip,
  Text,
  Select,
  useToast,
  Skeleton,
  SkeletonText,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCoreIndustryAsync,
  selectCoreIndustry,
  addCoreIndustryAsync,
  updateCoreIndustryAsync,
  deleteCoreIndustryAsync,
} from "../../../CoreIndustry/CoreIndustrySlice";
import { useForm } from "react-hook-form";
import { FaEye, FaEdit, FaTrashAlt } from "react-icons/fa";
import { DeleteIcon, EditIcon } from "@chakra-ui/icons";

function CoreIndustry() {
  const { register, handleSubmit, setValue, reset } = useForm();
  const dispatch = useDispatch();
  const coreIndustry = useSelector(selectCoreIndustry);
  const toast = useToast();

  const {
    isOpen: isAddOpen,
    onOpen: onAddOpen,
    onClose: onAddClose,
  } = useDisclosure();
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [deletingIndustry, setDeletingIndustry] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(fetchCoreIndustryAsync());
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        toast({
          title: "Error",
          description: "Failed to fetch core industries",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      }
    };
    fetchData();
  }, [dispatch, toast]);

  const handleOpenAdd = () => {
    reset();
    onAddOpen();
  };

  const handleOpenEdit = (industry) => {
    setSelectedIndustry(industry);
    setValue("title", industry.title);
    setValue("status", industry.status);
    onEditOpen();
  };

  const handleSave = async (data) => {
    try {
      await dispatch(addCoreIndustryAsync(data)).unwrap();
      await dispatch(fetchCoreIndustryAsync());
      onAddClose();
      reset();
      toast({
        title: "Success",
        description: "Core industry added successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      console.error("Failed to add industry:", error);
      toast({
        title: "Error",
        description: "Failed to add core industry",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  const handleOpenDelete = (industry) => {
    setDeletingIndustry(industry);
    onDeleteOpen();
  };

  const handleUpdate = async (data) => {
    if (!selectedIndustry) return;
    try {
      const updatedData = {
        uuid: selectedIndustry.uuid,
        coreIndustryData: {
          title: data.title,
          status: data.status,
        },
      };
      console.log("updated data" + updatedData);

      await dispatch(
        updateCoreIndustryAsync(updatedData, updatedData.coreIndustryData)
      ).unwrap();
      await dispatch(fetchCoreIndustryAsync());
      onEditClose();
      reset();
      setSelectedIndustry(null);
      toast({
        title: "Success",
        description: "Core industry updated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      console.error("Failed to update industry:", error);
      toast({
        title: "Error",
        description: "Failed to update core industry",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  const handleDelete = async () => {
    if (!deletingIndustry) return;
    try {
      await dispatch(deleteCoreIndustryAsync(deletingIndustry.uuid)).unwrap();
      await dispatch(fetchCoreIndustryAsync());
      onDeleteClose();
      setDeletingIndustry(null);
      toast({
        title: "Success",
        description: "Core industry deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      console.error("Failed to delete industry:", error);
      toast({
        title: "Error",
        description: "Failed to delete core industry",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  const SkeletonTable = () => (
    <TableContainer>
      <Table size="sm">
        <Thead className="bg-gray-200">
          <Tr>
            <Th>ID</Th>
            <Th>Title</Th>
            <Th>Status</Th>
            <Th>Action</Th>
          </Tr>
        </Thead>
        <Tbody>
          {[...Array(5)].map((_, index) => (
            <Tr key={index}>
              <Td>
                <Skeleton height="20px" width="30px" />
              </Td>
              <Td>
                <SkeletonText noOfLines={1} spacing="4" skeletonHeight="2" />
              </Td>
              <Td>
                <Skeleton height="20px" width="60px" />
              </Td>
              <Td>
                <HStack spacing={2}>
                  <Skeleton height="32px" width="32px" borderRadius="md" />
                  <Skeleton height="32px" width="32px" borderRadius="md" />
                </HStack>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );

  return (
    <div
      className="min-h-screen bg-adminbg p-4 contentAdmin"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div>
        <div className="w-full my-5 bg-white rounded-md">
          <div className="flex justify-between ">
            <p className="text-xl text-black font-semibold px-3 py-7">
              Core Industry List
            </p>
            <div className="py-4 px-3 flex gap-3">
              <Button
                colorScheme="blue"
                fontSize="medium"
                onClick={handleOpenAdd}
              >
                Add <i className="fa-solid fa-plus px-2"></i>
              </Button>
            </div>
          </div>

          {/* Add Core Industry Modal */}
          <Modal isOpen={isAddOpen} onClose={onAddClose} size="4xl">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader bg="blue.500" color="white" borderTopRadius="md">
                Add New Core Industry
              </ModalHeader>
              <ModalCloseButton color="white" />
              <form onSubmit={handleSubmit(handleSave)}>
                <ModalBody py={6}>
                  <VStack spacing={6}>
                    <Grid templateColumns="repeat(2, 1fr)" gap={4} width="100%">
                      <GridItem colSpan={2}>
                        <FormControl>
                          <FormLabel>Industry Title</FormLabel>
                          <Input
                            placeholder="Enter industry title"
                            className=" "
                            color="black"
                            {...register("title", { required: true })}
                          />
                        </FormControl>
                      </GridItem>
                      <GridItem colSpan={2}>
                        <FormControl>
                          <FormLabel>Status</FormLabel>
                          <Select
                            placeholder="Select status"
                            {...register("status", { required: true })}
                            borderColor="black"
                            color="black"
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </Select>

                        </FormControl>
                      </GridItem>
                    </Grid>
                  </VStack>
                </ModalBody>
                <ModalFooter bg="gray.50" borderBottomRadius="md">
                  <Button colorScheme="blue" mr={3} type="submit">
                    Save
                  </Button>
                  <Button onClick={onAddClose}>Cancel</Button>
                </ModalFooter>
              </form>
            </ModalContent>
          </Modal>

          {/* Edit Core Industry Modal */}
          <Modal isOpen={isEditOpen} onClose={onEditClose} size="4xl">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader bg="blue.500" color="white" borderTopRadius="md">
                Edit Core Industry
              </ModalHeader>
              <ModalCloseButton color="white" />
              <form onSubmit={handleSubmit(handleUpdate)}>
                <ModalBody py={6}>
                  <VStack spacing={6}>
                    <Grid templateColumns="repeat(2, 1fr)" gap={4} width="100%">
                      <GridItem colSpan={2}>
                        <FormControl>
                          <FormLabel>Industry Title</FormLabel>
                          <Input
                            placeholder="Enter industry title"
                            className=" "
                            color="black"
                            {...register("title", { required: true })}
                          />
                        </FormControl>
                      </GridItem>
                      <GridItem colSpan={2}>
                        <FormControl>
                          <FormLabel>Status</FormLabel>
                          <Select
                            placeholder="Select status"
                            {...register("status", { required: true })}
                            borderColor="black"
                            color="black"
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </Select>
                        </FormControl>
                      </GridItem>
                    </Grid>
                  </VStack>
                </ModalBody>
                <ModalFooter bg="gray.50" borderBottomRadius="md">
                  <Button colorScheme="blue" mr={3} type="submit">
                    Update
                  </Button>
                  <Button onClick={onEditClose}>Cancel</Button>
                </ModalFooter>
              </form>
            </ModalContent>
          </Modal>

          {/*Delete Modal*/}
          <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
            <ModalOverlay />
            <ModalContent maxW="2xl" borderradius="lg" boxShadow="lg">
              <ModalHeader fontSize="lg" fontWeight="bold" textAlign="center">
                Confirm Deletion
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Text fontSize="md" textAlign="center">
                  Are you sure you want to delete{" "}
                  <Text as="span" fontWeight="semibold" color="red.500">
                    "{deletingIndustry?.title}"
                  </Text>
                  ?
                </Text>
              </ModalBody>
              <ModalFooter display="flex" gap="2" justifyContent="center">
                <Button colorScheme="red" w="40" onClick={handleDelete}>
                  Delete
                </Button>
                <Button
                  variant="outline"
                  colorScheme="red"
                  w="40"
                  onClick={onDeleteClose}
                >
                  Cancel
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>

          {/* Table Display with Skeleton */}
          {isLoading ? (
            <SkeletonTable />
          ) : (
            <TableContainer>
              <Table size="sm">
                <Thead className="bg-gray-200">
                  <Tr>
                    <Th>ID</Th>
                    <Th>Title</Th>
                    <Th>Status</Th>
                    <Th>Action</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {coreIndustry.loading ? (
                    <Tr>
                      <Td colSpan="4" className="text-center py-4">
                        <Spinner size="lg" color="blue.500" />
                      </Td>
                    </Tr>
                  ) : coreIndustry.length > 0 ? (
                    coreIndustry.map((industry, index) => (
                      <Tr key={industry.uuid}>
                        <Td>{index + 1}</Td>
                        <Td>{industry.title}</Td>
                        <Td textColor="green">{industry.status}</Td>
                        <Td>
                          <HStack spacing={2}>
                            <Tooltip label="Edit Industry" placement="top">
                              <IconButton
                                icon={<EditIcon />}
                                colorScheme="blue"
                                size="sm"
                                onClick={() => handleOpenEdit(industry)}
                                aria-label="Edit industry"
                              />
                            </Tooltip>
                            <Tooltip label="Delete Industry" placement="top">
                              <IconButton
                                icon={<DeleteIcon />}
                                colorScheme="red"
                                size="sm"
                                onClick={() => handleOpenDelete(industry)}
                                aria-label="Delete industry"
                              />
                            </Tooltip>
                          </HStack>
                        </Td>
                      </Tr>
                    ))
                  ) : (
                    <Tr>
                      <Td colSpan="4" className="text-center py-4">
                        No data available
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </div>
      </div>
    </div>
  );
}

export default CoreIndustry;